import { forwardRef } from "react";

import {
  Button,
  CogFilledSVG,
  FlagHollowSVG,
  Icon,
  Toast,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import PropTypes from "prop-types";

import { handleDisableVocabularySupport } from "@/assignments/utils/assignmentActions";
import globalToasterRef from "@/base/globalToasterRef";
import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import StandaloneReadAloudButton from "../utils/StandaloneReadAloudButton";

import {
  aiIconCss,
  aiMessageCss,
  bodyWrapperCss,
  buttonContainerCss,
  definitionBodyCss,
  featureButtonCss,
  footerCss,
  keyTermCss,
  keyTermWrapperCss,
  readAloudButtonOverrideCss,
  readAloudIconCss,
} from "./KeyTermPopoutBody.style";

const propTypes = {
  contentId: PropTypes.string,
  currentFontSize: PropTypes.string,
  isStudent: PropTypes.bool,
  keyTerm: PropTypes.string,
  language: PropTypes.string,
  termDefinition: PropTypes.string,
};

const footerPropTypes = {
  onReportContent: PropTypes.func,
  toggleVocabularySupport: PropTypes.func,
  ...propTypes,
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const EVENT_TOAST_T3_VOCABULARY_DISABLED = {
  showIcon: true,
  statusColor: Toast.statusColor.success,
  children:
    "AI-generated vocabulary disabled for this article. Turn them back on in your assignment settings.",
};

const KeyTermPopoutBody = ({
  contentId,
  currentFontSize,
  isStudent,
  keyTerm,
  language,
  termDefinition,
}) => {
  const readAloudText = `${keyTerm}. ${termDefinition || ""}`;
  const readAloudEventProperties = {
    objectType: objectTypes.articleWord,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.start,
    streamProperties: {
      content_type: "Article",
      description: "T3 Vocabulary Read Aloud Initiated",
      content_id: contentId,
    },
  };

  return (
    <div css={bodyWrapperCss(!isStudent)}>
      <div css={keyTermWrapperCss}>
        <h3 css={keyTermCss(currentFontSize)}>{keyTerm}</h3>
        <StandaloneReadAloudButton
          dataProps={{
            "data-qa-selector": "key_term_read_aloud_button",
          }}
          language={language}
          text={readAloudText}
          iconCss={readAloudIconCss(currentFontSize)}
          buttonOverrideCss={readAloudButtonOverrideCss}
          eventProperties={readAloudEventProperties}
          powerWord={keyTerm}
        />
      </div>
      <div css={definitionBodyCss(currentFontSize)}>{termDefinition}</div>
    </div>
  );
};

const Footer = forwardRef(
  ({ contentId, onReportContent, keyTerm, toggleVocabularySupport }, ref) => {
    const { dispatch } = useAssignmentReducerContext();
    const IS_VOCABULARY_SUPPORT_ENABLED = false;

    const handleDisableVocabularySupportClick = () => {
      handleDisableVocabularySupport(
        { disable_vocabulary_support: true },
        dispatch,
      );
      toggleVocabularySupport(IS_VOCABULARY_SUPPORT_ENABLED);
      globalToasterRef.current.displayToast(EVENT_TOAST_T3_VOCABULARY_DISABLED);
    };

    return (
      <div css={footerCss}>
        <Icon
          __cssFor={aiIconCss}
          SvgComponent={AILunaTag}
          isDecorative
          size={Icon.sizes.sm}
        />
        <p css={aiMessageCss}>
          This word & its definition were generated by AI, and will only be
          displayed to students if you assign this article.
        </p>
        <div css={buttonContainerCss}>
          <ButtonWithTrackEvent
            __cssFor={{ root: featureButtonCss }}
            dataProps={{
              "data-qa-selector": "disable_key_term_content",
            }}
            flavor={Button.flavors.muted}
            icon={{
              SvgComponent: CogFilledSVG,
              alignment: Button.iconAlignments.left,
            }}
            onClick={() => {
              handleDisableVocabularySupportClick();
            }}
            objectType={objectTypes.generatedTextAsset}
            actionName={actionNames.hide}
            streamProperties={{
              content_type: "Article",
              description: `User clicked to disable key terms using button in key term: ${keyTerm}`,
              content_id: contentId,
            }}
          >
            Disable this feature
          </ButtonWithTrackEvent>
          <ButtonWithTrackEvent
            actionName={actionNames.flag}
            __cssFor={{ root: featureButtonCss }}
            dataProps={{
              "data-qa-selector": "report_key_term_content",
            }}
            flavor={Button.flavors.tertiary}
            icon={{
              SvgComponent: FlagHollowSVG,
              alignment: Button.iconAlignments.left,
            }}
            onClick={onReportContent}
            ref={ref}
            objectType={objectTypes.generatedTextAsset}
            actionPrefix={actionPrefixes.initiate}
            streamProperties={{
              content_type: "Article",
              description: `User clicked to report key term: ${keyTerm}`,
              content_id: contentId,
            }}
          >
            Report content
          </ButtonWithTrackEvent>
        </div>
      </div>
    );
  },
);
KeyTermPopoutBody.propTypes = propTypes;
Footer.propTypes = footerPropTypes;

KeyTermPopoutBody.Footer = Footer;

export default KeyTermPopoutBody;
